import { Box, SelectProps } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { MultiValue } from "react-select";

export interface SelectOption {
  value: any;
  label: any;
  subLabel?: any;
}

export interface CustomSelectProps extends SelectProps {
  name?: string;
  placeholder?: string;
  defaultSelectValue?: SelectOption;
  defaultMultiValues?: SelectOption[];
  options: SelectOption[];
  setValue?: (value: string) => void;
  onMultiChange?: (values: SelectOption[]) => void;
  isMulti?: boolean;
  isLoading?: boolean;
  isDynamicField?: boolean;
  width?: any;
  /** Renderiza o menu em portal no body — evita corte quando o select fica dentro
   *  de container com overflow (ex.: lista de colunas do relatório). */
  menuPortal?: boolean;
  /** Permite digitar valores livres além das opções (ex.: responsáveis antigos
   *  vindos do Excel, que não são usuários do sistema). */
  isCreatable?: boolean;
  /** Mostra o "x" para limpar a seleção (só faz sentido no modo single). */
  isClearable?: boolean;
  /** Applies the same visual invalid state used by native form controls. */
  isInvalid?: boolean;
}

const SearchableSelect = ({
  placeholder,
  defaultSelectValue,
  defaultMultiValues,
  setValue,
  onMultiChange,
  isMulti = false,
  isLoading,
  options,
  width,
  menuPortal = false,
  isCreatable = false,
  isClearable = false,
  isInvalid = false,
}: CustomSelectProps) => {
  const ref = useRef<any>(null);
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      borderColor: isInvalid ? "#FC8181" : base.borderColor,
      boxShadow: isInvalid ? "0 0 0 1px #FC8181" : base.boxShadow,
      "&:hover": {
        borderColor: isInvalid ? "#FC8181" : base.borderColor,
      },
    }),
    ...(menuPortal
      ? { menuPortal: (base: any) => ({ ...base, zIndex: 2000 }) }
      : {}),
  };
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);

  const filterOptions = (inputValue: string) =>
    options.filter((i) =>
      i.label?.toLowerCase().includes(inputValue.toLowerCase())
    );

  useEffect(() => {
    if (!isMulti && defaultSelectValue) {
      setSelectedOption(defaultSelectValue);
    }
  }, [defaultSelectValue, isMulti]);

  useEffect(() => {
    if (isMulti && defaultMultiValues) {
      setSelectedOptions(defaultMultiValues);
    }
  }, [defaultMultiValues, isMulti]);

  const loadOptions = (
    inputValue: string,
    callback: (options: SelectOption[]) => void
  ) => {
    callback(filterOptions(inputValue));
  };

  const handleSelect = (option: SelectOption | null) => {
    setSelectedOption(option);
    setValue?.(option?.value);
  };

  const handleMultiSelect = (vals: MultiValue<SelectOption>) => {
    const arr = Array.from(vals);
    setSelectedOptions(arr);
    onMultiChange?.(arr);
  };

  if (isMulti) {
    const MultiComponent: any = isCreatable ? AsyncCreatableSelect : AsyncSelect;
    return (
      <Box w={width} ref={ref}>
        <MultiComponent
          isMulti
          value={selectedOptions}
          placeholder={placeholder ?? "Selecione..."}
          defaultOptions={options}
          isLoading={isLoading}
          aria-invalid={isInvalid}
          onChange={handleMultiSelect as any}
          loadOptions={loadOptions}
          styles={selectStyles}
          {...(isCreatable
            ? {
                formatCreateLabel: (input: string) => `Usar "${input}"`,
                createOptionPosition: "first" as const,
              }
            : {})}
          {...(menuPortal
            ? {
                menuPortalTarget: document.body,
                menuPosition: "fixed" as const,
              }
            : {})}
        />
      </Box>
    );
  }

  const SingleComponent: any = isCreatable ? AsyncCreatableSelect : AsyncSelect;

  return (
    <Box w={width} ref={ref}>
      <SingleComponent
        value={selectedOption}
        placeholder={placeholder ?? "Todos"}
        defaultOptions={options}
        isLoading={isLoading}
        isClearable={isClearable}
        aria-invalid={isInvalid}
        onChange={handleSelect as any}
        loadOptions={loadOptions}
        styles={selectStyles}
        {...(isCreatable
          ? {
              formatCreateLabel: (input: string) => `Usar "${input}"`,
              createOptionPosition: "first" as const,
            }
          : {})}
        {...(menuPortal
          ? {
              menuPortalTarget: document.body,
              menuPosition: "fixed" as const,
            }
          : {})}
      />
    </Box>
  );
};

export default SearchableSelect;
